"use client";

import { findProfile, PROFILES } from "@kedaat/definitions";
import {
  addDays,
  formatPlainDate,
  israelDateOf,
  ZmanimEvaluator,
  type PlainDate,
  type ZmanId,
} from "@kedaat/engine";
import { findLocality, LOCALITIES, type Locality } from "@kedaat/locations";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState, type ReactNode, type TouchEvent } from "react";
import { useEngine, useNow } from "@/lib/hooks";
import { sanitizeSettings, settingsToQuery, storeSettings } from "@/lib/settings";
import { SITE_URL } from "@/lib/site";
import { skyColorAt, skyMarks } from "@/lib/sky";
import { useSettings } from "@/lib/useSettings";
import { computeDay, pointFor } from "@/lib/zmanim";
import { DateNav } from "./DateNav";
import { DayHeader } from "./DayHeader";
import { DayTimeline } from "./DayTimeline";
import { LocalityPicker } from "./LocalityPicker";
import { NextZmanCard } from "./NextZmanCard";
import { OpinionDialog } from "./OpinionDialog";
import { ProfilePicker } from "./ProfilePicker";
import { ReportDialog } from "./ReportDialog";
import { SkyBackground } from "./SkyBackground";
import { SpecialTimesCard } from "./SpecialTimesCard";
import { ZmanimList } from "./ZmanimList";

/** A horizontal swipe longer than this (in pixels) moves to another day. */
const SWIPE_DISTANCE = 70;

export function ZmanimApp({
  pageLocality,
  fallback,
}: {
  /** On a locality's own page, that locality is shown and kept out of the query string. */
  pageLocality?: number;
  /** Shown until the calculation engine has loaded (e.g. server-rendered zmanim). */
  fallback?: ReactNode;
}) {
  const router = useRouter();
  const engine = useEngine();
  const now = useNow();
  const { settings, update, ready, urlDate } = useSettings(pageLocality);
  const [date, setDate] = useState<PlainDate | null>(null);
  const [openZman, setOpenZman] = useState<ZmanId | null>(null);
  const [copied, setCopied] = useState(false);
  const [reporting, setReporting] = useState(false);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (ready && !date) setDate(urlDate ?? israelDateOf(Date.now()));
  }, [ready, urlDate, date]);

  const locality = findLocality(settings.locality) ?? LOCALITIES[0]!;
  const profile = findProfile(settings.profile) ?? PROFILES[0]!;
  const candleMinutes = settings.candleMinutes[locality.code] ?? locality.candle.minutes;
  const today = now === null ? null : israelDateOf(now);
  const isToday = !!date && !!today && formatPlainDate(date) === formatPlainDate(today);
  const queryDate = isToday ? null : date;

  useEffect(() => {
    if (!date) return;
    const query = settingsToQuery(settings, queryDate, {
      omitCity: settings.locality === pageLocality,
    });
    if (window.location.search !== query) window.history.replaceState(null, "", query);
  }, [settings, date, queryDate, pageLocality]);

  const selectLocality = (l: Locality) => {
    if (pageLocality === undefined || l.code === pageLocality) {
      update(() => ({ locality: l.code }));
      return;
    }
    // On a locality page, go to the chosen locality's own page, keeping the other settings.
    const next = sanitizeSettings({ ...settings, locality: l.code });
    storeSettings(next);
    router.push(`/city/${l.slug}/${settingsToQuery(next, queryDate, { omitCity: true })}`);
  };

  const evaluator = useMemo(
    () => (engine ? new ZmanimEvaluator(engine.astro, pointFor(locality)) : null),
    [engine, locality],
  );

  const day = useMemo(
    () =>
      evaluator && engine && date
        ? computeDay(evaluator, engine.calendar, date, profile, settings.overrides, candleMinutes)
        : null,
    [evaluator, engine, date, profile, settings.overrides, candleMinutes],
  );

  const openRow = day?.rows.find((r) => r.zman.id === openZman) ?? null;

  // The page is washed in the color of the sky right now, but only while today is on screen.
  const skyColor = useMemo(() => {
    if (!day || !isToday || now === null) return null;
    const marks = skyMarks(day.rows);
    return marks ? skyColorAt(now, marks) : null;
  }, [day, isToday, now]);

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  const moveDay = (days: number) => date && setDate(addDays(date, days));

  // In a right-to-left layout the future lies to the left, so dragging right shows the next day.
  const onTouchStart = (e: TouchEvent) => {
    const touch = e.touches[0];
    touchStart.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
  };
  const onTouchEnd = (e: TouchEvent) => {
    const start = touchStart.current;
    const touch = e.changedTouches[0];
    touchStart.current = null;
    if (!start || !touch) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) > SWIPE_DISTANCE && Math.abs(dx) > Math.abs(dy) * 2) moveDay(dx > 0 ? 1 : -1);
  };

  const dateNav = (className: string) =>
    date && (
      <DateNav
        className={className}
        date={date}
        isToday={isToday}
        onChange={setDate}
        onPrevious={() => moveDay(-1)}
        onNext={() => moveDay(1)}
        onToday={() => today && setDate(today)}
      />
    );

  return (
    <div className="space-y-5">
      <SkyBackground color={skyColor} />
      <p
        role="note"
        className="flex items-start gap-2 rounded-xl bg-warn-soft px-3 py-2 text-xs leading-relaxed text-warn"
      >
        <span aria-hidden="true">⚠</span>
        <span>
          <strong>האתר בפיתוח.</strong> השיטות טרם אומתו על ידי רב או מומחה לוחות — אין להסתמך על
          הזמנים למעשה.
        </span>
      </p>

      <section className="grid gap-3 sm:grid-cols-2">
        <LocalityPicker locality={locality} onSelect={selectLocality} />
        <ProfilePicker
          profile={profile}
          overrideCount={Object.keys(settings.overrides).length}
          onSelect={(id) => update(() => ({ profile: id, overrides: {} }))}
          onResetOverrides={() => update(() => ({ overrides: {} }))}
        />
      </section>

      {dateNav("hidden sm:flex")}

      {day && date ? (
        <div className="space-y-5" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <div key={formatPlainDate(date)} className="kd-rise space-y-5">
            <DayHeader info={day.info} locality={locality} />
            {isToday && <NextZmanCard rows={day.rows} now={now} seconds={settings.seconds} />}
            <DayTimeline rows={day.rows} now={isToday ? now : null} />
          </div>
          <SpecialTimesCard
            special={day.special}
            locality={locality}
            seconds={settings.seconds}
            onCandleMinutesChange={(minutes) =>
              update((s) => {
                const { [locality.code]: _previous, ...rest } = s.candleMinutes;
                return {
                  candleMinutes:
                    minutes === locality.candle.minutes
                      ? rest
                      : { ...rest, [locality.code]: minutes },
                };
              })
            }
          />
          <ZmanimList
            rows={day.rows}
            now={isToday ? now : null}
            seconds={settings.seconds}
            onOpen={setOpenZman}
          />
          <p className="text-sm text-muted">
            לחצו על זמן כדי להשוות בין השיטות ולבחור שיטה אחרת.{" "}
            <a href="/methods/" className="font-medium text-accent hover:underline">
              פירוט כל השיטות
            </a>
          </p>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={settings.seconds}
                onChange={(e) => update(() => ({ seconds: e.target.checked }))}
                className="size-4 accent-[var(--color-accent)]"
              />
              הצגת שניות (ללא עיגול לדקות)
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={share}
                className="inline-flex min-h-10 items-center rounded-xl border border-line bg-card px-3 text-sm font-medium hover:bg-accent-soft"
              >
                {copied ? "הקישור הועתק ✓" : "העתקת קישור לתצוגה זו"}
              </button>
              <button
                type="button"
                onClick={() => setReporting(true)}
                aria-haspopup="dialog"
                className="inline-flex min-h-10 items-center rounded-xl border border-line bg-card px-3 text-sm font-medium hover:bg-accent-soft"
              >
                דיווח על אי-התאמה
              </button>
            </div>
          </div>

          {/* On phones the day switcher stays at the bottom of the screen. */}
          {dateNav(
            "sticky bottom-3 z-20 flex rounded-2xl border border-line bg-card/95 p-2 shadow-lg backdrop-blur sm:hidden",
          )}

          <ReportDialog
            context={
              reporting
                ? {
                    locality,
                    date,
                    profile,
                    day,
                    url: typeof window === "undefined" ? SITE_URL : window.location.href,
                  }
                : null
            }
            onClose={() => setReporting(false)}
          />

          <OpinionDialog
            row={openRow}
            profileOpinionId={openRow ? profile.opinions[openRow.zman.id] : null}
            evaluator={evaluator}
            date={date}
            locality={locality}
            seconds={settings.seconds}
            onChoose={(opinionId) => {
              const zman = openRow!.zman.id;
              update((s) => ({ overrides: { ...s.overrides, [zman]: opinionId } }));
            }}
            onClose={() => setOpenZman(null)}
          />
        </div>
      ) : (
        (fallback ?? (
          <div className="space-y-2" aria-busy="true" aria-label="מחשב זמנים">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-2xl bg-card" />
            ))}
          </div>
        ))
      )}
    </div>
  );
}
